/*
 * LogRocket sanitizers. Session replay masks inputs, but network bodies, headers, page URLs and
 * console output are recorded verbatim — so every credential (passwords, one-time login codes,
 * auth tokens) is redacted here before LogRocket stores it. Ids, including link codes, stay readable.
 */
import _ from 'lodash';
import { getOperationName } from '@apollo/client/utilities';

import { PARENT_VERIFY_TAKEOVER, VERIFY_PARENT_CONTACT } from '../queries/parent.mutations';

export const REDACTED = '[REDACTED]';

// Matches anywhere in a key: confirmPassword, reportCardAccessToken.
const isSensitiveKey = (key: unknown) => typeof key === 'string' && /password|token/i.test(key);

// Their `code` is a one-time login code; every other `code` is a player or invite id.
const ONE_TIME_CODE_OPERATIONS = [VERIFY_PARENT_CONTACT, PARENT_VERIFY_TAKEOVER].map(getOperationName);

const BEARER = /Bearer\s+[\w\-.~+/]+=*/gi;
const JWT = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const QUERY_PARAM = /([?&])([^=&#\s]+)=([^&#\s]*)/g;
// Bodies with nothing credential-shaped skip the parse/redact/serialize pass.
const MAY_HOLD_CREDENTIAL = new RegExp(['password', 'token', 'bearer', 'eyJ', ...ONE_TIME_CODE_OPERATIONS].join('|'), 'i');

const SENSITIVE_HEADERS = ['authorization'];
const CONSOLE_LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;

type ConsoleLevel = typeof CONSOLE_LEVELS[number];
type ConsoleSink = Record<ConsoleLevel, (...args: unknown[]) => void>;

interface NetworkEntry {
  url?: string;
  body?: string;
  headers: { [key: string]: string | undefined };
}

export const redactText = (text: string) => text
  .replace(BEARER, `Bearer ${REDACTED}`)
  .replace(JWT, REDACTED)
  .replace(QUERY_PARAM, (match, separator, name) => (isSensitiveKey(name) ? `${separator}${name}=${REDACTED}` : match));

const redactValue = (value: unknown): unknown => _.cloneDeepWith(value, (child, key) => {
  if (typeof child === 'string') return isSensitiveKey(key) ? REDACTED : redactText(child);
  return undefined;
});

// Mutates the already-cloned body; handles a single operation or a batched array.
const redactOneTimeCodes = (operations: unknown) => _.castArray(operations).forEach((operation) => {
  if (ONE_TIME_CODE_OPERATIONS.includes(_.get(operation, 'operationName')) && _.has(operation, 'variables.code')) {
    _.set(operation as object, 'variables.code', REDACTED);
  }
});

export const redactBody = (body: string | undefined) => {
  if (!body || !MAY_HOLD_CREDENTIAL.test(body)) return body;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return redactText(body);
  }
  const redacted = redactValue(parsed);
  redactOneTimeCodes(redacted);
  return JSON.stringify(redacted);
};

const redactHeaders = (headers: NetworkEntry['headers']) => _.mapValues(
  headers,
  (value, name) => (SENSITIVE_HEADERS.includes(name.toLowerCase()) ? REDACTED : value),
);

export const sanitizeRequest = <T extends NetworkEntry & { url: string }>(request: T): T => ({
  ...request,
  url: redactText(request.url),
  body: redactBody(request.body),
  headers: redactHeaders(request.headers),
});

export const sanitizeResponse = <T extends NetworkEntry>(response: T): T => ({
  ...response,
  body: redactBody(response.body),
  headers: redactHeaders(response.headers),
});

// Only strings and plain data are copied; Errors, DOM nodes and class instances pass through as-is.
export const redactConsoleArg = (arg: unknown) => (
  typeof arg === 'string' || Array.isArray(arg) || _.isPlainObject(arg) ? redactValue(arg) : arg
);

// LogRocket's console capture has no sanitizer, so it is disabled and fed a redacted copy instead.
export const forwardRedactedConsole = (sink: ConsoleSink) => {
  const warn = console.warn.bind(console);
  let forwarding = false;
  CONSOLE_LEVELS.forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      if (forwarding) return;
      forwarding = true;
      try {
        sink[level](...args.map(redactConsoleArg));
      } catch (err) {
        warn('forwardRedactedConsole: failed to send a console entry to LogRocket', err);
      } finally {
        forwarding = false;
      }
    };
  });
};
